import {
	forwardRef,
	Fragment,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';

import { ConversationStatus } from '@ekhodealer/ekho-common';
import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	Button,
	Chip,
	Divider,
	Empty,
	Group,
	SkeletonRect,
	Stack,
	Text,
	TextInput,
} from '@ekhodealer/ekho-common/rev';

import type { SearchConversationsParams } from '@common/api/conversations/useSearchConversations';
import type {
	ConversationChannel,
	ConversationListRow,
	ConversationStatus as ConversationStatusType,
} from '@ekhodealer/ekho-common';

import { useConversationFacets } from '@common/api/conversations/useConversationFacets';
import { useSearchConversations } from '@common/api/conversations/useSearchConversations';
import NoConversations from '@common/components/icons/NoConversations';
import { formatChannelLabel } from '@common/lib/strings';

import { ConversationListItem } from './ConversationListItem';
import { CONVERSATION_STATUS_CONFIG } from './conversationStatusConfig';
import { ConversationStatusTag } from './ConversationStatusTag';

import './ConversationList.scss';

interface ConversationListProps {
	/** ID of the currently selected conversation, or null if none. */
	selectedId: string | null;
	/** Callback invoked when the user selects a conversation row. */
	onSelect: (conversation: ConversationListRow) => void;
	/** Optional search/filter parameters forwarded to the search query. */
	searchParams?: SearchConversationsParams;
	/**
	 * When set, the list will auto-select (call onSelect) for this conversationId
	 * once data loads. Used for deep-linking from a URL param.
	 */
	autoSelectId?: string;
	/**
	 * When true, escalated conversations are fetched in a separate query
	 * and displayed in their own section above the main list.
	 * Only active when no user-selected status filters are applied.
	 */
	showEscalatedSection?: boolean;
}

export interface ConversationListHandle {
	/** Programmatically focus the first item in the list. */
	focusFirstItem: () => void;
}

const STATUS_FILTER_OPTIONS = Object.fromEntries(
	Object.entries(CONVERSATION_STATUS_CONFIG).map(([key, { label }]) => [key, label]),
) as Record<ConversationStatusType, string>;

const CHANNEL_FILTER_KEYS: ConversationChannel[] = ['sms', 'web_chat'];

const NON_ESCALATED_STATUSES: ConversationStatusType[] = Object.values(ConversationStatus).filter(
	(s): s is ConversationStatusType => s !== ConversationStatus.Escalated,
);

export const ConversationList = forwardRef<ConversationListHandle, ConversationListProps>(
	({ selectedId, onSelect, searchParams, autoSelectId, showEscalatedSection = false }, ref) => {
		const listRef = useRef<HTMLDivElement>(null);
		const [searchTerm, setSearchTerm] = useState('');
		const [statusFilters, setStatusFilters] = useState<ConversationStatusType[]>([]);
		const [channelFilters, setChannelFilters] = useState<ConversationChannel[]>([]);

		const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
		const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

		const handleSearchChange = useCallback((value: string): void => {
			setSearchTerm(value);
			if (debounceRef.current) clearTimeout(debounceRef.current);
			debounceRef.current = setTimeout(() => setDebouncedSearchTerm(value.trim()), 300);
		}, []);

		// When showEscalatedSection is active and no status filters are set,
		// split into two queries: one for escalated, one for everything else.
		const isSplitMode = showEscalatedSection && statusFilters.length === 0;

		const mainParams = useMemo<SearchConversationsParams>(
			() => ({
				...searchParams,
				...(debouncedSearchTerm ? { query: debouncedSearchTerm } : {}),
				filters: {
					...searchParams?.filters,
					...(statusFilters.length > 0 ? { status: statusFilters } : {}),
					...(channelFilters.length > 0 ? { channels: channelFilters } : {}),
					...(isSplitMode ? { status: NON_ESCALATED_STATUSES } : {}),
				},
			}),
			[searchParams, debouncedSearchTerm, statusFilters, channelFilters, isSplitMode],
		);

		const escalatedParams = useMemo<SearchConversationsParams>(
			() => ({
				...searchParams,
				...(debouncedSearchTerm ? { query: debouncedSearchTerm } : {}),
				filters: {
					...searchParams?.filters,
					status: [ConversationStatus.Escalated],
					...(channelFilters.length > 0 ? { channels: channelFilters } : {}),
				},
			}),
			[searchParams, debouncedSearchTerm, channelFilters],
		);

		const {
			conversations,
			isLoading,
			isError,
			refetch,
			hasNextPage,
			isFetchingNextPage,
			fetchNextPage,
		} = useSearchConversations(mainParams);

		const {
			conversations: escalatedConversations,
			isLoading: isEscalatedLoading,
		} = useSearchConversations(escalatedParams, { enabled: isSplitMode });

		const { channelCounts, statusCounts } = useConversationFacets(searchParams);

		// All conversations across both queries, for auto-select and keyboard nav
		const allConversations = useMemo<ConversationListRow[]>(
			() => (isSplitMode ? [...escalatedConversations, ...conversations] : conversations),
			[isSplitMode, escalatedConversations, conversations],
		);

		// Auto-select a conversation when deep-linking via URL param
		const autoSelectedRef = useRef(false);
		useEffect(() => {
			if (!autoSelectId || autoSelectedRef.current || isLoading || isEscalatedLoading) return;
			const match = allConversations.find((c) => c.conversationId === autoSelectId);
			if (match) {
				autoSelectedRef.current = true;
				onSelect(match);
			}
		}, [autoSelectId, allConversations, isLoading, isEscalatedLoading, onSelect]);

		const sentinelRef = useRef<HTMLDivElement>(null);

		useEffect(() => {
			const sentinel = sentinelRef.current;
			if (!sentinel) return;

			const observer = new IntersectionObserver(
				(entries) => {
					if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
						fetchNextPage();
					}
				},
				{ rootMargin: '200px' },
			);
			observer.observe(sentinel);
			return (): void => observer.disconnect();
		}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

		const hasActiveFilters = statusFilters.length > 0 || channelFilters.length > 0;
		const activeFilterCount = statusFilters.length + channelFilters.length;

		useImperativeHandle(ref, () => ({
			focusFirstItem: (): void => {
				const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
				items?.[0]?.focus();
			},
		}));

		const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>): void => {
			const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
			if (!items) return;
			const currentIndex = Array.from(items).indexOf(document.activeElement as HTMLElement);

			if (event.key === 'ArrowDown') {
				event.preventDefault();
				items[currentIndex < items.length - 1 ? currentIndex + 1 : 0]?.focus();
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				items[currentIndex > 0 ? currentIndex - 1 : items.length - 1]?.focus();
			}
		}, []);

		const handleStatusSelectionChange = useCallback((keys: Iterable<string>): void => {
			setStatusFilters(Array.from(keys) as ConversationStatusType[]);
		}, []);

		const handleChannelSelectionChange = useCallback((keys: Iterable<string>): void => {
			setChannelFilters(Array.from(keys) as ConversationChannel[]);
		}, []);

		const removeStatusFilter = useCallback((status: ConversationStatusType): void => {
			setStatusFilters((prev) => prev.filter((s) => s !== status));
		}, []);

		const removeChannelFilter = useCallback((channel: ConversationChannel): void => {
			setChannelFilters((prev) => prev.filter((c) => c !== channel));
		}, []);

		const clearAllFilters = useCallback((): void => {
			setStatusFilters([]);
			setChannelFilters([]);
		}, []);

		const handleSelect = useCallback(
			(conversationId: string): void => {
				const conversation = allConversations.find(
					(c) => c.conversationId === conversationId,
				);
				if (conversation) onSelect(conversation);
			},
			[allConversations, onSelect],
		);

		const renderListContent = (): React.ReactNode => {
			if (isLoading || (isSplitMode && isEscalatedLoading)) {
				return (
					<Stack itemsSpacing='8' padding={{ y: 12, right: 12 }}>
						{Array.from({ length: 5 }, (_, i) => (
							<SkeletonRect key={i} width='100%' height={96} rounding='8' />
						))}
					</Stack>
				);
			}

			if (isError) {
				return (
					<Stack itemsSpacing='12' padding={{ y: 32 }} itemsAlignX='center'>
						<Text size='body' color='secondary' align='center'>
							Failed to load conversations.
						</Text>
						<Button variant='ghost' size='small' onPress={() => refetch()}>
							Try again
						</Button>
					</Stack>
				);
			}

			const hasEscalated = isSplitMode && escalatedConversations.length > 0;

			if (!hasEscalated && conversations.length === 0) {
				return (
					<Empty
						icon={<NoConversations />}
						title={hasActiveFilters ? 'No conversations match' : 'No conversations yet'}
						description={
							hasActiveFilters
								? 'Try adjusting your filters.'
								: 'Conversations will appear here once they begin.'
						}
					/>
				);
			}

			return (
				<div
					ref={listRef}
					className='conversation-list__items'
					role='listbox'
					aria-label='Conversations'
					onKeyDown={handleKeyDown}>
					{hasEscalated && (
						<>
							<Stack padding={{ x: 16, top: 8, bottom: 4 }}>
								<Text size='bodySmall' color='secondary'>
									Escalated
								</Text>
								<Divider />
							</Stack>
							{escalatedConversations.map((conversation, index) => (
								<Fragment key={conversation.conversationId}>
									<ConversationListItem
										conversation={conversation}
										isSelected={selectedId === conversation.conversationId}
										onSelect={handleSelect}
									/>
									{index < escalatedConversations.length - 1 && <Divider />}
								</Fragment>
							))}
							{conversations.length > 0 && (
								<Stack padding={{ x: 16, top: 8, bottom: 4 }}>
									<Text size='bodySmall' color='secondary'>
										All conversations
									</Text>
									<Divider />
								</Stack>
							)}
						</>
					)}
					{conversations.map((conversation, index) => (
						<Fragment key={conversation.conversationId}>
							<ConversationListItem
								conversation={conversation}
								isSelected={selectedId === conversation.conversationId}
								onSelect={handleSelect}
							/>
							{index < conversations.length - 1 && <Divider />}
						</Fragment>
					))}
					<div ref={sentinelRef} />
				</div>
			);
		};

		return (
			<div className='conversation-list'>
				<Stack itemsSpacing='8' padding={{ y: 12, right: 12 }}>
					<TextInput
						placeholder='Search'
						type='search'
						size='medium'
						value={searchTerm}
						onChange={handleSearchChange}
					/>
					<Group itemsSpacing='8' itemsAlignX='start'>
						<ActionMenuTrigger>
							<Button variant='secondary' size='small'>
								Status{statusFilters.length > 0 ? ` (${statusFilters.length})` : ''}
							</Button>
							<ActionMenu
								placement='bottom start'
								selectionMode='multiple'
								selectedKeys={new Set(statusFilters)}
								onSelectionChange={handleStatusSelectionChange}>
								{Object.entries(STATUS_FILTER_OPTIONS).map(([value]) => {
									const count = statusCounts[value as ConversationStatusType];
									return (
										<ActionMenuItem
											key={value}
											id={value}
											label={
												<Group itemsSpacing='6' itemsAlignY='center'>
													<ConversationStatusTag
														status={value as ConversationStatusType}
													/>
													<Text size='footnote' color='secondary'>
														{count ?? 0}
													</Text>
												</Group>
											}
										/>
									);
								})}
							</ActionMenu>
						</ActionMenuTrigger>
						<ActionMenuTrigger>
							<Button variant='secondary' size='small'>
								Channel
								{channelFilters.length > 0 ? ` (${channelFilters.length})` : ''}
							</Button>
							<ActionMenu
								placement='bottom start'
								selectionMode='multiple'
								selectedKeys={new Set(channelFilters)}
								onSelectionChange={handleChannelSelectionChange}>
								{CHANNEL_FILTER_KEYS.map((value) => {
									const count = channelCounts[value];
									return (
										<ActionMenuItem
											key={value}
											id={value}
											label={
												<Group itemsSpacing='6' itemsAlignY='center'>
													<Text size='bodySmall'>
														{formatChannelLabel(value)}
													</Text>
													<Text size='footnote' color='secondary'>
														{count ?? 0}
													</Text>
												</Group>
											}
										/>
									);
								})}
							</ActionMenu>
						</ActionMenuTrigger>
					</Group>
				</Stack>

				{hasActiveFilters && (
					<Stack padding={{ y: 4, right: 12 }} itemsSpacing='8'>
						<Group>
							<Group itemsSpacing='6' itemsAlignY='center' itemsAlignX='start'>
								{statusFilters.map((status) => (
									<Chip
										key={status}
										size='xsmall'
										appearance='outline'
										onClose={() => removeStatusFilter(status)}>
										{STATUS_FILTER_OPTIONS[status]}
									</Chip>
								))}
								{channelFilters.map((channel) => (
									<Chip
										key={channel}
										size='xsmall'
										appearance='outline'
										onClose={() => removeChannelFilter(channel)}>
										{formatChannelLabel(channel)}
									</Chip>
								))}
							</Group>
							<Button variant='underlined' size='small' onPress={clearAllFilters}>
								Clear all ({activeFilterCount})
							</Button>
						</Group>
						<Divider />
					</Stack>
				)}

				{renderListContent()}
			</div>
		);
	},
);

ConversationList.displayName = 'ConversationList';
